/** IXD Preview 테이블 컴포넌트 (개별 스크롤 가능, 좌우 overflow 시 그라데이션 표시) */

import { useMemo } from "react";
import type { PeriodType } from "@/apis/compare/route";
import {
  createTableSemanticResolver,
  type CellChangeOrigin,
} from "@/utils/ixd-cell-model";
import { TableScrollArea } from "@/toggle-ui-kit/table-scroll-area";
import type { TableCellColumn } from "../table-cell";
import { useDlViewConfig } from "./dl-view-context";
import { TableBodyCell } from "./table-body-cell";

interface TableViewProps {
  periodType: PeriodType;
  periodLabel: string;
  rows: Array<{ columns: TableCellColumn[] }>;
  cellChanges?: Record<string, string>;
  changeOrigins?: Record<string, CellChangeOrigin>;
  cellIdentityByNormalizedCellId?: Map<string, string>;
  identityChanges?: Map<string, string>;
  onCellEdit?: (
    cellId: string,
    newValue: string,
    identityKey?: string,
    locationUid?: string,
  ) => void;
}

const EMPTY_TABLE_ROWS: TableViewProps["rows"] = [];

export const TableView = ({
  periodType,
  periodLabel,
  rows,
  cellChanges,
  changeOrigins,
  cellIdentityByNormalizedCellId,
  identityChanges,
  onCellEdit,
}: TableViewProps) => {
  const { isTransposed, labelMap, showPeriodLabel } = useDlViewConfig();

  const tableRows = rows ?? EMPTY_TABLE_ROWS;
  const semanticResolver = useMemo(
    () =>
      createTableSemanticResolver({
        rows: tableRows,
        periodType,
        isTransposed,
        labelMap,
      }),
    [tableRows, periodType, isTransposed, labelMap],
  );

  if (tableRows.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-[0.75rem]" data-period-type={periodType}>
      {showPeriodLabel && (
        <div
          data-ixd-period-label
          className="label-2-medium text-label-alternative"
        >
          {periodLabel}
        </div>
      )}
      <TableScrollArea>
        <table className="w-max border-collapse">
          <tbody>
            {tableRows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {row.columns.map((column, colIndex) => {
                  const semanticContext = semanticResolver({
                    rowIndex,
                    colIndex,
                    column,
                  });
                  return (
                    <TableBodyCell
                      key={`${column.id}-${colIndex}`}
                      column={column}
                      periodType={periodType}
                      rowIndex={rowIndex}
                      colIndex={colIndex}
                      semanticContext={semanticContext}
                      cellChanges={cellChanges}
                      changeOrigins={changeOrigins}
                      cellIdentityByNormalizedCellId={
                        cellIdentityByNormalizedCellId
                      }
                      identityChanges={identityChanges}
                      onCellEdit={onCellEdit}
                    />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </TableScrollArea>
    </div>
  );
};
